/**
 * Auth helpers — thin wrappers around the Supabase client.
 *
 * The React-facing surface lives in `lib/auth.tsx` (provider + hooks). This
 * module is for non-component callers (route loaders, mutation handlers).
 */
import { supabase } from '../supabase'

export interface MagicLinkOptions {
  /** Optional override for the post-callback landing path. Defaults to /. */
  redirectTo?: string
  /**
   * If the user is accepting an invitation, threading the token through the
   * callback URL lets the callback page bounce them back to /accept-invite.
   */
  inviteToken?: string
  /**
   * Pre-fill the email field for invitation flows so a typo locks the user
   * out of their invite.
   */
  shouldCreateUser?: boolean
}

/**
 * Send a magic-link OTP to `email`. The Supabase client emits a `SIGNED_IN`
 * event once the recipient clicks the link and lands on /auth/callback.
 *
 * Throws `Error` with the Supabase-provided message on failure.
 */
export async function sendMagicLink(email: string, opts: MagicLinkOptions = {}): Promise<void> {
  const params = new URLSearchParams()
  if (opts.redirectTo) params.set('next', opts.redirectTo)
  if (opts.inviteToken) params.set('invite', opts.inviteToken)
  const qs = params.toString()
  const callback = `${window.location.origin}/auth/callback${qs ? `?${qs}` : ''}`

  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: callback,
      shouldCreateUser: opts.shouldCreateUser ?? true,
    },
  })
  if (error) throw new Error(error.message)
}

/** Sign out the current session. No-op when already signed out. */
export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut()
  if (error) throw new Error(error.message)
}

/**
 * Read the current session synchronously from the in-memory cache. Useful in
 * route loaders / API helpers where we don't want a network round trip.
 */
export async function getCurrentSession() {
  const { data, error } = await supabase.auth.getSession()
  if (error) throw new Error(error.message)
  return data.session
}

/**
 * Resolve the role-appropriate landing path from a session's user_metadata.
 * Mirrors the server-side `redirectForRole` so the SPA can route locally
 * after sign-in without a round trip.
 */
export function landingPathForRole(role: string | null | undefined): string {
  if (role === 'actor') return '/actor'
  if (role === 'supervisor') return '/supervisor'
  if (role === 'owner' || role === 'member') return '/producer'
  // No role yet — likely an invitation that wasn't completed. Send to account.
  return '/account'
}
